import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { FirestoreService } from './firestore/firestore.service';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import 'rxjs/add/operator/mergeMap';

import { Globals } from '../globals';
import { firestore } from 'firebase/app';
import { SupplierBillId, ChargeNote, Outcomed } from './supplier-bill.service';
import { Utils } from '../util';

export interface Outcome {
  active: number;
  createdAt: any;
  updatedAt: any;

  supplierRef: any;
  fiscalDataRef: any;
  bankRef: any;
  
  date: string;
  amount: number;
  payMethod: string;
  reference: string;
  notes: string;

  bills: OutcomeBill[];
}

interface OutcomeId extends Outcome {
  id: string;
}

export interface OutcomeBill {
  supplierBillId: string;
  amount: number;
}

@Injectable()
export class OutcomeService {

  outcomeCollection: AngularFirestoreCollection<Outcome>;
  outcomes: any;

  outcomeDocument: AngularFirestoreDocument<OutcomeId>;
  outcome: Observable<OutcomeId>;

  collection: string;
  supplierCollection: string;
  fiscalDataCollection: string;
  bankCollection: string;

  // NOTE: Change this any for Outcome Interface
  outcome$: Observable<any>;
  outcomes$: Observable<any[]>;

  constructor(
    private afs: AngularFirestore,
    private afh: FirestoreService,
    private router: Router
  ) {
    this.collection = Globals.OUTCOME_COLLECTION;
    this.supplierCollection = Globals.CUSTOMER_SUPPLIER_COLLECTION;
    this.fiscalDataCollection = Globals.FISCAL_DATA_COLLECTION;
    this.bankCollection = Globals.BANK_COLLECTION;
  }

  /**
   * Get all outcomes with references
   * @return {Observable} Arrays of outcomes
   */
  getOutcomes() {
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('active', '==', true) ).map(documents => {
      return documents.map(document => {
        return Observable.combineLatest(
          this.afh.docWithIds$(`${document.supplierRef.path}`),
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          this.afh.docWithIds$(`${document.bankRef.path}`),
          (supplierData, fiscalDataData, bankData) => ({ supplier: supplierData, fiscalData: fiscalDataData, bank: bankData })
        ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, bankObj: data.bank, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
   * Get all outcomes with references
   * @return {Observable} Arrays of outcomes
   */
  getOutcomesFifteenDays() {
    let filteredDate = new Date();
    filteredDate.setDate(filteredDate.getDate() - 15);
    let date = this.stringifyDate(filteredDate);
    return this.afh.colWithIds$(this.collection, ref => ref.orderBy('date','desc').where('date', '>', date).where('active', '==', true) ).map(documents => {
      return documents.map(document => {
        return Observable.combineLatest(
          this.afh.docWithIds$(`${document.supplierRef.path}`),
          this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
          this.afh.docWithIds$(`${document.bankRef.path}`),
          (supplierData, fiscalDataData, bankData) => ({ supplier: supplierData, fiscalData: fiscalDataData, bank: bankData })
        ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, bankObj: data.bank, ...document }) );
      });
    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Get outcome data
  * @param {string} sid - The id of the outcome
  * @return {Observable} The Outcome Object
  */
  getOutcome(sid: string) {
    return this.outcome$ = this.afh.docWithIds$(this.collection + '/' + sid).map(document => {
      return Observable.combineLatest(
        this.afh.docWithIds$(`${document.supplierRef.path}`),
        this.afh.docWithIds$(`${document.fiscalDataRef.path}`),
        this.afh.docWithIds$(`${document.bankRef.path}`),
        (supplierData, fiscalDataData, bankData) => ({ supplier: supplierData, fiscalData: fiscalDataData, bank: bankData })
      ).map( data => ({ supplierObj: data.supplier, fiscalDataObj: data.fiscalData, bankObj: data.bank, ...document }) );

    }).mergeMap(documents => Observable.combineLatest(documents));
  }

  /**
  * Create new outcome
  * @param {Outcome} outcome - The data of the outcome
  */
  addOutcome({...outcome}: Outcome) {
    outcome.supplierRef = this.afh.doc(`${this.supplierCollection}/${outcome.supplierRef}`).ref;
    outcome.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${outcome.fiscalDataRef}`).ref;
    outcome.bankRef = this.afh.doc(`${this.bankCollection}/${outcome.bankRef}`).ref;
    
    const docId = this.afs.createId();
    const ref = this.afs.collection(this.collection).doc(docId);
    ref.set({active: true, createdAt: `${Utils.today}T${Utils.time}`, ...outcome});
    return docId;
  }

  /**
   * Update current outcome
   * @param {string} sid  - The id of the outcome
   * @param {Outcome} outcome - The data of the outcome
   */
  updateOutcome(sid: string, {...outcome}: Outcome) {
    outcome.supplierRef = this.afh.doc(`${this.supplierCollection}/${outcome.supplierRef}`).ref;
    outcome.fiscalDataRef = this.afh.doc(`${this.fiscalDataCollection}/${outcome.fiscalDataRef}`).ref;
    outcome.bankRef = this.afh.doc(`${this.bankCollection}/${outcome.bankRef}`).ref;
    this.afh.update(`${this.collection}/${sid}`, outcome);
  }

  /**
   * Delete current outcome
   * @param {string} sid - The id of the outcome
   */
  deleteOutcome(sid: string) {
    this.afh.delete(`${this.collection}/${sid}`);
  }

  windowReplace(action: string, id?: string){
    switch(action){
      case 'add':
      case 'create':
        this.router.navigate([`/new_outcome/${id}`]);
        break;
      case 'edit':
      case 'update':
        this.router.navigate([`/edit_outcome/${id}`]);
        break;
      case 'view':
      case 'display':
        this.router.navigate([`/vw_outcome/${id}`]);
        break;
      case 'read':
        this.router.navigate([`/outcome`]);
        break;
      default:
        console.log('Not an action');
    }
  }


  get today(){
    return this.stringifyDate(new Date());
  }
  stringifyDate(date: Date){
    var dd:string = date.getDate() + '';
    var mm:string = date.getMonth()+ 1 + '';
    var yyyy = date.getFullYear();
    if(+dd<10) dd = '0'+dd;
    if(+mm<10) mm = '0'+mm;
    return yyyy + '-' + mm + '-' + dd;
  }
}

export class OutcomeData {
  //IN CASE OF ORDER CHANGE,
  //DONT CHANGE KEYS (USED FOR DATABASE), CHANGE INDEX ATTRIBUTE
  statusObjects = {
      '001': { name: 'Efectivo'},
      '002': { name: 'Cheque Nominativo'},
      '003': { name: 'Transferencia electrónica de fondos'},
      '004': { name: 'Tarjeta de crédito'},
      '005': { name: 'Tarjeta de débito'},
      '999': { name: 'Por definir'}
  }
}

export class OutcomedBill {
  
  supplierBill: SupplierBillId;
  lastAmount: number;
  amount: number;

  constructor(supplierBill: SupplierBillId, outcomeId?: string){
    this.supplierBill = supplierBill;
    let outcomed = outcomeId ?  supplierBill.outcomeds.find(outcomed => outcomed.outcomeRef.id == outcomeId) : undefined;
    this.lastAmount = outcomed ? outcomed.amount : 0;
    this.amount = this.lastAmount;
  }

  get isValid(): boolean { return !this.isOver && this.amount >= 0 }

  get isUnder(): boolean { return this.residue > 0; }
  get isComplete(): boolean { return this.residue == 0; }
  get isOver(): boolean { return this.residue < 0; }

  get databaseObject(): OutcomeBill { return {supplierBillId: this.supplierBill.id, amount: this.amount}; }

  get folio(): string { return this.supplierBill.folio; }
  get chargeNotes(): ChargeNote[] { return this.supplierBill.chargeNotes; }
  get outcomeIds(): string[] { return this.supplierBill.outcomeds.map(({outcomeRef: {id}}) => id); }

  get billTotal(): number { return this.supplierBill.total; }
  get chargeNotesTotal(): number { return this.supplierBill.chargeNoteTotal; }
  get otherOutcomesTotal(): number { return this.outcomedTotal - this.lastAmount; }
  get total(): number { return this.billTotal - (this.chargeNotesTotal + this.otherOutcomesTotal); }
  get outcomedTotal(): number { return this.supplierBill.outcomeds.reduce( (total, outcomed) => total + outcomed.amount, 0); }

  get residue(): number { let residue = this.total - this.amount; return Utils.strToNumber(Utils.money(residue)); }
}

export class OutcomeFilter {
  allRows: any[];

  dateIndex: number;
  supplierIndex: number;
  bankNumberIndex: number;

  constructor(allRows: any[], {dateIndex=0, supplierIndex=0, bankNumberIndex=0}){
    this.allRows = allRows;
    this.dateIndex = dateIndex;
    this.supplierIndex = supplierIndex;
    this.bankNumberIndex = bankNumberIndex;

    let suppliers = new Set( allRows.map(row => row.supplierObj.name) );
    let sortedSuppliers = [...suppliers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.supplierSelect = ['Todos', ...sortedSuppliers].map(name => ({name}) );

    let bankNumbers = new Set( allRows.map(row => row.bankNumber) );
    let sortedBankNumbers = [...bankNumbers].sort((it1, it2) => it1 > it2 ? 1 : -1);
    this.bankNumberSelect = ['Todas', ...sortedBankNumbers].map(name => ({name}) );
  }

  get filteredData() {
    return this.allRows
      .filter(({date}) => {
        let rowDate = new Date(date);
        let filterDate = new Date();
        let {amount, unit} = this.dateSelect[this.dateIndex];
        switch(unit){
          case 'day': filterDate.setDate(filterDate.getDate() - amount); break;
          case 'month': filterDate.setMonth(filterDate.getMonth() - amount); filterDate.setDate(0); break;
          case 'year': filterDate = new Date(filterDate.getFullYear() - amount, 0); break;
          default: filterDate.setFullYear(2015); break;
        }
        if(rowDate >= filterDate) return 1;
      })
      .filter(({supplierName: rowName}) => {
        let {name: filterName} = this.supplierSelect[this.supplierIndex];
        if(rowName == filterName || filterName == 'Todos') return 1;
      })
      .filter(({bankNumber: rowName}) => {
        let {name: filterName} = this.bankNumberSelect[this.bankNumberIndex];
        if(rowName == filterName || filterName == 'Todas') return 1;
      });
  }

  get json(): string {
    return JSON.stringify({
      dateIndex: this.dateIndex,
      supplierIndex: this.supplierIndex,
      bankNumberIndex: this.bankNumberIndex
    });
  }

  dateSelect = [
    { amount: 15, unit: 'day', name: '15 días' },
    { amount: 0, unit: 'month', name: 'Este mes' },
    { amount: 6, unit: 'month', name: '6 meses' },
    { amount: 0, unit: 'year', name: 'Este año' },
    { amount: 0, unit: 'forever', name: 'Siempre' },
  ];
  supplierSelect = [];
  bankNumberSelect = [];
}
